import { Injectable } from '@angular/core';
import { NotificationHlp } from 'src/lib/fliko/helpers/notification.helper';
import { ReplaceMap } from 'src/lib/fliko/interfaces/replaceMap';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import {
  NotificationStatus,
  NotificationType,
  TargetAddress,
  NotificationTarget,
  NotificationQueueData
} from 'src/lib/fliko/models/messageQueue/notification.data';
import { NewsData } from 'src/lib/fliko/models/news/news.data';
import { ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { BookingBan } from 'src/lib/fliko/models/users/user.data';
import { BookingDetails } from 'src/app/view/booking/service/model/booking.model';
import { BookingCalendarDetailsViewModel } from 'src/app/view/booking/service/model/booking.view-model';
import { NewsFrequency } from 'src/app/view/news/service/models/news-frequency';
import { PollVM } from 'src/app/view/poll/service/models/poll.view-model';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { NotificationHelper } from '../helpers/notification.helper';
import { LocalizationHelper } from '../helpers/localization.helper';
import { BasicNotificationVM } from '../models/notification-view-model';
import { LocalizationService } from './localization.service';
import { DatabaseService } from './database.service';
import { PushIdService } from './pushId.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly locHelper = new LocalizationHelper(this.ls);

  constructor(
    private dbSrv: DatabaseService,
    private errorHandlingService: ErrorHandlingService,
    private ls: LocalizationService,
    private pushId: PushIdService
  ) {}

  /**
   * @path /notificationQueue/
   * @key id
   */
  private get notificationQueue() {
    return this.dbSrv.commonFactory.getCollection<NotificationQueueData>('notificationQueue');
  }

  // #region Database

  public cancelNotification(id: string): Promise<void> {
    return this.notificationQueue.updateElement(id, { status: NotificationStatus.CANCELED });
  }

  public async sendNotificationToQueue(notification: NotificationQueueData): Promise<string> {
    await this.notificationQueue.setElement(notification.id, notification);
    return notification.id;
  }

  // #endregion Database

  // #region Generic
  public async sendNotification(vm: BasicNotificationVM): Promise<string> {
    const notificationData: NotificationQueueData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      ...vm,
      body: vm.body ? vm.body.substring(0, 100) : null,
      additionalData: {
        ...vm.additionalData,
        replaceMap: vm.additionalData.replaceMap ? ReplaceMap.sanitize(vm.additionalData.replaceMap) : null
      }
    };
    return this.sendNotificationToQueue(notificationData);
  }
  // #endregion Resolution

  // #region Booking

  public async sendNotificationOnBookingBlock(
    estateId: string,
    banData: BookingBan,
    locationName: string,
    adminName: string,
    blockedUserId: string
  ): Promise<void> {
    if (!blockedUserId) {
      return;
    }
    const duration = this.locHelper.getDurationHR(banData.banStartDate, banData.banEndDate, true);
    const notificationData: NotificationQueueData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      estateId: estateId,
      titleKey: 'booking.generic.title',
      bodyKey: 'booking.ban.body.alt',
      target: { userIds: [blockedUserId] },
      deepLink: 'booking',
      additionalData: {
        type: NotificationType.BOOKING,
        replaceMap: ReplaceMap.sanitize({
          $admin: adminName,
          $duration: duration,
          $location: locationName,
          $reason: banData.banReason
        })
      }
    };
    await this.sendNotificationToQueue(notificationData);
  }

  public async sendNotificationOnBookingChange(
    bookingDetails: BookingDetails,
    locationName: string,
    adminName: string
  ): Promise<void> {
    if (!bookingDetails.userId) {
      return;
    }
    const notificationData: NotificationQueueData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      estateId: bookingDetails.estateId,
      titleKey: 'booking.change.title',
      bodyKey: 'booking.change.body',
      deepLink: `booking/${bookingDetails.id}`,
      target: { userIds: [bookingDetails.userId] },
      additionalData: {
        type: NotificationType.BOOKING,
        replaceMap: ReplaceMap.sanitize({
          $admin: adminName,
          $location: locationName
        })
      }
    };
    await this.sendNotificationToQueue(notificationData);
  }

  public async sendNotificationOnBookingCancel(
    bookingDetails: BookingCalendarDetailsViewModel,
    adminName: string
  ): Promise<void> {
    if (!bookingDetails.userDetails?.id) {
      return;
    }
    const date = this.locHelper.localizeDate(bookingDetails.bookingStart, 'dtShort');
    const notificationData: NotificationQueueData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      estateId: bookingDetails.estateId,
      titleKey: 'booking.cancel.title',
      bodyKey: 'booking.cancel.body',
      deepLink: `booking/${bookingDetails.id}`,
      target: { userIds: [bookingDetails.userDetails!.id] },
      additionalData: {
        type: NotificationType.BOOKING,
        replaceMap: ReplaceMap.sanitize({
          $admin: adminName,
          $date: date,
          // Remove '(anulowana)' from end of name (if exist)
          $location: bookingDetails.locationName.split('(anulowana)')[0]
        })
      }
    };
    await this.sendNotificationToQueue(notificationData);
  }

  // #endregion Booking

  // #region News

  public async sendNotificationOnNews(
    news: NewsData,
    estate: EstateData | null,
    residentTypes: ResidentType[] | null,
    notificationReceivers?: TargetAddress[],
    newsChanged: boolean = false
  ): Promise<string> {
    let target: NotificationTarget;
    if (news.receivers) {
      if (notificationReceivers) {
        target = { address: notificationReceivers, residentTypes };
      } else {
        this.errorHandlingService.showGlobalError(this.ls.translate('error.badReceivers'));
        return '';
      }
    } else {
      target = {
        address: [{ block: '', stairway: '', number: '' }],
        residentTypes
      };
    }

    const basicData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      body: news.title?.substring(0, 100),
      deepLink: `news/${news.id}`,
      estateId: news.estateId,
      target: target,
      additionalData: {
        type: NotificationType.NEWS,
        foreignId: news.id,
        replaceMap: ReplaceMap.sanitize({ $estate: estate?.name ?? '' })
      }
    };

    const notificationData: NotificationQueueData = {
      ...basicData,
      titleKey: `news.${newsChanged ? 'update' : 'add'}.title`,
      sendDate: news.notificationTimestamp ?? NotificationHelper.getNewsSendTime(news.dateFrom).valueOf()
    };
    return await this.sendNotificationToQueue(notificationData);
  }

  // TODO: deprecated, serial news
  public async sendNotificationRemindersOnNews(
    news: NewsData,
    estate: EstateData,
    frequency?: NewsFrequency,
    notificationReceivers?: TargetAddress[]
  ): Promise<string[]> {
    let target: NotificationTarget;
    if (news.receivers) {
      if (notificationReceivers) {
        target = { address: notificationReceivers };
      } else {
        this.errorHandlingService.showGlobalError(this.ls.translate('error.badReceivers'));
        return [];
      }
    } else {
      target = { address: [{ block: '', stairway: '', number: '' }] };
    }

    const basicData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      body: news.title?.substring(0, 100),
      deepLink: `news/${news.id}`,
      estateId: estate.id,
      target: target,
      additionalData: { type: NotificationType.NEWS, foreignId: news.id }
    };

    const promises: Promise<string>[] = [];
    if (frequency) {
      const dates = NotificationHelper.getNewsReminderNotificationDates(news.dateFrom, news.dateTo, frequency);
      dates.forEach((date) => {
        const notificationData: NotificationQueueData = {
          ...basicData,
          titleKey: 'news.rem.title',
          sendDate: NotificationHelper.getNewsSendTime(date).valueOf(),
          additionalData: {
            type: NotificationType.NEWS,
            foreignId: news.id,
            reminder: true,
            replaceMap: ReplaceMap.sanitize({ $estate: estate.name })
          }
        };
        promises.push(this.sendNotificationToQueue(notificationData));
      });
    }
    return await Promise.all(promises);
  }

  // #endregion News

  // #region Polls

  public async sendNotificationOnPollsReminder(pollData: PollVM, estateId: string, userIds: string[]) {
    if (!userIds.length) {
      return;
    }
    const notificationData: NotificationQueueData = {
      ...NotificationHlp.buildBasicData(this.pushId.create()),
      estateId,
      titleKey: 'poll.rem.title',
      bodyKey: 'poll.rem.body',
      deepLink: `poll/${pollData.id}`,
      target: { userIds },
      additionalData: {
        type: NotificationType.POLL,
        foreignId: pollData.id,
        replaceMap: ReplaceMap.sanitize({ $name: pollData.title })
      }
    };
    await this.sendNotificationToQueue(notificationData);
  }
  // #endregion Polls
}
